import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowDown,
  faArrowUp,
  faCalendarCheck,
  faCheck,
  faClock,
  faEdit,
  faPhone,
  faPlus,
  faTrash,
  faUser,
} from '@fortawesome/free-solid-svg-icons';
import AppHeader from '../../../components/AppHeader.tsx';
import WaitingListModal from '../../../components/WaitingListModal';
import {
  WaitingListItem,
  WaitingListStatus,
  deleteWaitingList,
  fetchWaitingList,
  isExactSlot,
  markBooked,
  markContacted,
  trimTime,
  updateWaitingList,
} from '@/features/waitingList/api';
import { useToast } from '@/hooks/useToast';

const statusBadge: Record<WaitingListStatus, { label: string; cls: string }> = {
  ACTIVE: { label: 'Čeká', cls: 'bg-amber-100 text-amber-800' },
  CONTACTED: { label: 'Kontaktován', cls: 'bg-blue-100 text-blue-800' },
  BOOKED: { label: 'Zarezervováno', cls: 'bg-green-100 text-green-800' },
  EXPIRED: { label: 'Expirováno', cls: 'bg-gray-100 text-gray-700' },
  CANCELLED: { label: 'Zrušeno', cls: 'bg-red-100 text-red-800' },
  NO_RESPONSE: { label: 'Bez odpovědi', cls: 'bg-orange-100 text-orange-800' },
};

const formatTermin = (item: WaitingListItem): string => {
  const dFrom = dayjs(item.dateFrom).format('D.M.');
  const dTo = dayjs(item.dateTo).format('D.M.YYYY');
  const tFrom = trimTime(item.timeFrom);
  const tTo = trimTime(item.timeTo);
  if (isExactSlot(item)) {
    return `${dayjs(item.dateFrom).format('D.M.YYYY')} ${tFrom}`;
  }
  const dateRange = item.dateFrom === item.dateTo ? dayjs(item.dateFrom).format('D.M.YYYY') : `${dFrom}–${dTo}`;
  if (tFrom && tTo) return `${dateRange} · ${tFrom}–${tTo}`;
  return `${dateRange} · kdykoli`;
};

const WaitingListPage: React.FC = () => {
  const { entityId } = useParams<{ entityId: string }>();
  const toast = useToast();
  const [items, setItems] = useState<WaitingListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<WaitingListStatus>('ACTIVE');

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<WaitingListItem | null>(null);

  const [contactingId, setContactingId] = useState<string | null>(null);
  const [contactNote, setContactNote] = useState('');

  const refresh = async () => {
    if (!entityId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchWaitingList(entityId, { status: statusFilter, size: 100 });
      setItems(res.content);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e?.message ?? 'Chyba načítání');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, statusFilter]);

  const handleDelete = async (item: WaitingListItem) => {
    if (!entityId) return;
    if (!confirm(`Smazat záznam pro ${item.customer.firstName} ${item.customer.lastName}?`)) return;
    try {
      await deleteWaitingList(entityId, item.id);
      toast.success('Smazáno', '');
      void refresh();
    } catch (e: any) {
      toast.error('Chyba', e?.response?.data?.message ?? 'Smazání selhalo');
    }
  };

  const handleMarkContacted = async (item: WaitingListItem) => {
    if (!entityId) return;
    try {
      await markContacted(entityId, item.id, contactNote || '');
      setContactingId(null);
      setContactNote('');
      toast.success('Kontakt zaznamenán', '');
      void refresh();
    } catch (e: any) {
      toast.error('Chyba', e?.response?.data?.message ?? 'Operace selhala');
    }
  };

  const handlePriority = async (item: WaitingListItem, delta: number) => {
    if (!entityId) return;
    try {
      await updateWaitingList(entityId, item.id, { priority: item.priority + delta });
      void refresh();
    } catch (e: any) {
      toast.error('Chyba', e?.response?.data?.message ?? 'Operace selhala');
    }
  };

  // Mark booked je v MVP zatím manuální — tlačítko vyzve obsluhu zalozit
  // rezervaci v kalendari a vlozit ID. Plnohodnotny prefill CreateBookingModal
  // prijde v navazujici praci.
  const handleBookManually = async (item: WaitingListItem) => {
    if (!entityId) return;
    const bookingId = prompt(
      `Vytvořte rezervaci pro ${item.customer.firstName} ${item.customer.lastName} v kalendáři, pak vložte ID rezervace (UUID):`
    );
    if (!bookingId) return;
    try {
      await markBooked(entityId, item.id, bookingId.trim());
      toast.success('Záznam označen jako zarezervovaný', '');
      void refresh();
    } catch (e: any) {
      toast.error('Chyba', e?.response?.data?.message ?? 'Operace selhala');
    }
  };

  const breadcrumb = [{ label: 'Rezervace' }, { label: 'Čekací listina' }];

  return (
    <div className="h-full flex flex-col">
      <AppHeader title="Čekací listina" breadcrumb={breadcrumb} />

      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="p-6 space-y-6">
          <section className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Čekací listina</h1>
                <p className="text-gray-600 mt-1">
                  Klienti čekající na uvolnění slotu. Při zrušení rezervace se zobrazí matching kandidáti.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <select
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value as WaitingListStatus)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="ACTIVE">Aktivní</option>
                  <option value="CONTACTED">Kontaktovaní</option>
                  <option value="BOOKED">Zarezervovaní</option>
                  <option value="EXPIRED">Expirovaní</option>
                  <option value="CANCELLED">Zrušení</option>
                  <option value="NO_RESPONSE">Bez odpovědi</option>
                </select>
                <button
                  onClick={() => {
                    setEditing(null);
                    setModalOpen(true);
                  }}
                  className="bg-amber-600 text-white px-4 py-2 rounded-lg hover:bg-amber-700 transition-colors flex items-center gap-2"
                >
                  <FontAwesomeIcon icon={faPlus} />
                  Přidat
                </button>
              </div>
            </div>
          </section>

          <section className="bg-white rounded-lg shadow-sm border border-gray-200">
            {error && <div className="p-4 bg-red-50 border-b border-red-200 text-sm text-red-700">{error}</div>}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Klient</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Služba</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Termín</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Zaměstnanec</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priorita</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stav</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Akce</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading && (
                    <tr>
                      <td colSpan={7} className="px-4 py-6 text-center text-sm text-gray-500">
                        Načítám…
                      </td>
                    </tr>
                  )}
                  {!loading && items.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-4 py-12 text-center text-sm text-gray-500">
                        Žádné záznamy v této kategorii.
                      </td>
                    </tr>
                  )}
                  {!loading &&
                    items.map(item => (
                      <React.Fragment key={item.id}>
                        <tr className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm">
                            <div className="font-medium text-gray-900 flex items-center gap-2">
                              <FontAwesomeIcon icon={faUser} className="text-gray-400" />
                              {item.customer.firstName} {item.customer.lastName}
                            </div>
                            {item.customer.phone && (
                              <div className="text-xs text-gray-500 flex items-center gap-1 ml-5 mt-0.5">
                                <FontAwesomeIcon icon={faPhone} className="w-3 h-3" />
                                {item.customer.phone}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">{item.service.name}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            <div className="flex items-center gap-2">
                              <FontAwesomeIcon icon={faClock} className="text-gray-400 w-3 h-3" />
                              {formatTermin(item)}
                            </div>
                            {item.notes && <div className="text-xs text-gray-500 mt-0.5 max-w-xs truncate">{item.notes}</div>}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">{item.preferredWorker?.displayName ?? '—'}</td>
                          <td className="px-4 py-3 text-sm">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handlePriority(item, 1)}
                                title="Zvýšit prioritu"
                                className="p-1 text-gray-400 hover:text-green-600"
                              >
                                <FontAwesomeIcon icon={faArrowUp} className="w-3 h-3" />
                              </button>
                              <span className="w-8 text-center font-medium">{item.priority}</span>
                              <button
                                onClick={() => handlePriority(item, -1)}
                                title="Snížit prioritu"
                                className="p-1 text-gray-400 hover:text-red-600"
                              >
                                <FontAwesomeIcon icon={faArrowDown} className="w-3 h-3" />
                              </button>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusBadge[item.status].cls}`}>
                              {statusBadge[item.status].label}
                            </span>
                            {item.contactedAt && (
                              <div className="text-xs text-gray-500 mt-0.5">{dayjs(item.contactedAt).format('D.M. HH:mm')}</div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-right whitespace-nowrap">
                            <div className="flex items-center justify-end gap-1">
                              {item.status === 'ACTIVE' && (
                                <>
                                  <button
                                    onClick={() => {
                                      setContactingId(item.id);
                                      setContactNote('');
                                    }}
                                    title="Kontaktováno"
                                    className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                                  >
                                    <FontAwesomeIcon icon={faCheck} />
                                  </button>
                                  <button
                                    onClick={() => handleBookManually(item)}
                                    title="Zarezervovat"
                                    className="p-2 text-green-600 hover:bg-green-50 rounded"
                                  >
                                    <FontAwesomeIcon icon={faCalendarCheck} />
                                  </button>
                                </>
                              )}
                              <button
                                onClick={() => {
                                  setEditing(item);
                                  setModalOpen(true);
                                }}
                                title="Upravit"
                                className="p-2 text-gray-600 hover:bg-gray-100 rounded"
                              >
                                <FontAwesomeIcon icon={faEdit} />
                              </button>
                              <button
                                onClick={() => handleDelete(item)}
                                title="Smazat"
                                className="p-2 text-red-600 hover:bg-red-50 rounded"
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            </div>
                          </td>
                        </tr>
                        {contactingId === item.id && (
                          <tr className="bg-blue-50">
                            <td colSpan={7} className="px-4 py-3">
                              <div className="flex items-start gap-3">
                                <textarea
                                  value={contactNote}
                                  onChange={e => setContactNote(e.target.value)}
                                  placeholder="Co řekl klient? (nebrala telefon, posunula na jindy, ...)"
                                  rows={2}
                                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                />
                                <div className="flex flex-col gap-2">
                                  <button
                                    onClick={() => handleMarkContacted(item)}
                                    className="px-4 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
                                  >
                                    Uložit
                                  </button>
                                  <button
                                    onClick={() => {
                                      setContactingId(null);
                                      setContactNote('');
                                    }}
                                    className="px-4 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200"
                                  >
                                    Zrušit
                                  </button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </div>

      {entityId && (
        <WaitingListModal
          isOpen={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setEditing(null);
          }}
          onSaved={() => void refresh()}
          salonId={entityId}
          mode={editing ? 'edit' : 'create'}
          existing={editing ?? undefined}
        />
      )}
    </div>
  );
};

export default WaitingListPage;
